/**
 * The store.
 *
 * **No money moves.** The server refuses to grant anything unless it was
 * started with stub purchasing explicitly switched on, so this is the real flow
 * — validate, grant, record — with the payment step missing rather than faked.
 * The screen says so plainly instead of pretending otherwise, because a button
 * that looks like it charges you and does not is worse than an honest one.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatPrice, purchase, purchaseThemeRpc } from '../net/session.js';
import type { CatalogPack } from '../net/session.js';
import { ThemeCard } from './ThemeCard.js';
import type { ThemeOffer } from './ThemeCard.js';
import { hasAccount, useSession } from '../session-context.js';
import { useCatalog } from '../use-catalog.js';
import { GameCard } from './GameCard.js';

export function Store() {
  const state = useSession();
  const { session } = state;
  const { catalog, error, retry, refresh } = useCatalog();
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  /** Which item a guest tried to buy, so the prompt can name it. */
  const [asking, setAsking] = useState<string | null>(null);

  if (error) {
    return (
      <div className="page narrow felt-page store-page">
        <h1 className="page-title page-heading">Store</h1>
        <p className="banner error">{error}</p>
        <button type="button" className="big-button" onClick={retry}>
          Try again
        </button>
      </div>
    );
  }

  if (!catalog) return <div className="splash">Dealing…</div>;

  /*
   * A purchase needs an account, and this is the moment to say so (D88).
   *
   * Most players are guests authenticated by a device id in local storage. A
   * purchase attached to that evaporates when somebody clears their data or
   * picks up a different phone, and "I paid and it is gone" is the worst first
   * support ticket there is.
   *
   * Asked at the buy, not at the door. Making everybody register before they
   * can look at a shop is how you get fewer of both - and the account they
   * make here KEEPS what they already have, because registering links the
   * email to the session in hand rather than starting a new one.
   */
  const needsAccount = !hasAccount(state);

  const run = async (id: string, buy: () => Promise<{ ok: boolean; message?: string }>) => {
    if (!session || busy) return;
    if (needsAccount) {
      setAsking(id);
      return;
    }
    setBusy(id);
    setFailed(null);
    try {
      const result = await buy();
      if (!result.ok) {
        setFailed(result.message ?? 'That could not be bought.');
        return;
      }
      // Entitlements changed, so anything cached about them is now wrong.
      await refresh();
    } catch {
      setFailed('Could not reach the server.');
    } finally {
      setBusy(null);
    }
  };

  const buyPack = (pack: CatalogPack) => run(pack.id, () => purchase(session!, pack.id));

  /*
   * The shop's two lists, derived rather than declared.
   *
   * A pack that grants themes is a cosmetic offer and belongs with them; a
   * pack that grants a seat upgrade is not and does not. Reading that off what
   * a pack CONTAINS means adding a second seat upgrade, or a third holiday
   * pair, needs no change here.
   */
  const cosmeticPacks = catalog.packs.filter((pack) => pack.themeIds.length > 0);
  const upgrades = catalog.packs.filter((pack) => pack.themeIds.length === 0);

  /* Themes a pack already carries are not offered singly; see the note below. */
  const inAPack = new Set(cosmeticPacks.flatMap((pack) => pack.themeIds));
  const themeById = new Map(catalog.themes.map((theme) => [theme.id, theme]));

  const count = (ids: string[]) => {
    const decks = ids.length;
    const cast = ids.reduce(
      (total, id) => total + (themeById.get(id)?.contents.avatarIds.length ?? 0),
      0,
    );
    return `${String(decks * 54)} cards · ${String(decks)} table${decks === 1 ? '' : 's'} · ${String(cast)} faces`;
  };

  const offers: ThemeOffer[] = [
    ...cosmeticPacks.map((pack) => ({
      id: pack.id,
      name: pack.name,
      description: pack.description,
      priceCents: pack.priceCents,
      owned: pack.owned,
      deckIds: pack.themeIds.map((id) => themeById.get(id)?.contents.deckId ?? id),
      contents: count(pack.themeIds),
    })),
    ...catalog.themes
      .filter((theme) => !theme.free && !inAPack.has(theme.id))
      .map((theme) => ({
        id: theme.id,
        name: theme.name,
        description: theme.description,
        priceCents: theme.priceCents,
        owned: theme.owned,
        deckIds: [theme.contents.deckId],
        contents: count([theme.id]),
      })),
  ];

  const buyOffer = (offer: ThemeOffer) =>
    run(offer.id, () =>
      themeById.has(offer.id)
        ? purchaseThemeRpc(session!, offer.id)
        : purchase(session!, offer.id),
    );

  return (
    <div className="page felt-page store-page">
      <h1 className="page-title page-heading">Store</h1>

      <p className="page-lede">Packs, decks and tables to make the game yours.</p>

      <p className="banner warn">
        Nothing here charges you. Buying is not switched on yet — these grant
        immediately so the rest can be tried out.
      </p>

      {failed ? <p className="banner error">{failed}</p> : null}

      {/*
        Why a guest is being asked to register in the middle of buying (D88).
        It keeps what they already have, and saying so is the difference
        between a reasonable request and a toll gate.
      */}
      {asking !== null ? (
        <div className="form-card account-ask">
          <p className="setting-title">Make an account first</p>
          <p className="field-help">
            Anything you buy is kept on your account rather than on this
            browser, so it is still yours on your next phone. You keep the name,
            the face and the games you already have.
          </p>
          <div className="account-ask-row">
            <Link className="cta cta-primary" to="/register">
              <span className="cta-label">Create an account</span>
            </Link>
            <button type="button" className="text-button" onClick={() => setAsking(null)}>
              Not now
            </button>
          </div>
        </div>
      ) : null}

      {/*
        What is for sale, drawn rather than described.

        A theme's whole value is what it looks like, and the shop was listing
        it as a sentence. Packs and singles share one grid because to somebody
        buying they are the same thing - a price and what you get for it - and
        the only difference is that a holiday pair gets you two.

        A theme carried by a pack is NOT listed on its own. Both cost the same,
        so the single would be the same money for half as much, and a shop that
        shows you the worse deal beside the better one is wasting your time.
      */}
      {offers.length > 0 ? (
        <>
          <h2 className="section-title">Decks and tables</h2>
          <div className="theme-grid">
            {offers.map((offer) => (
              <ThemeCard
                key={offer.id}
                offer={offer}
                currency={catalog.currency}
                busy={busy === offer.id}
                onBuy={() => void buyOffer(offer)}
              />
            ))}
          </div>
        </>
      ) : null}

      {/* The seat upgrade, which is not a cosmetic and does not pretend to be
          one - there is nothing to preview. */}
      {upgrades.length > 0 ? (
        <>
          <h2 className="section-title">More room at the table</h2>
          <div className="pack-list">
            {upgrades.map((pack) => (
              <div className="pack" key={pack.id}>
                <div className="pack-text">
                  <span className="pack-name">{pack.name}</span>
                  <span className="pack-blurb">{pack.description}</span>
                </div>
                {pack.owned ? (
                  <span className="owned-mark">Yours</span>
                ) : (
                  <button
                    type="button"
                    className="pill-button buy"
                    disabled={busy !== null}
                    onClick={() => void buyPack(pack)}
                  >
                    {busy === pack.id ? 'Getting…' : formatPrice(pack.priceCents, catalog.currency)}
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      ) : null}

      {/*
        Games that cost something, and nothing else.

        This was every game there is, which made the bottom two thirds of the
        shop a second copy of the library - thirteen things nobody has to buy,
        under the two things they do. A store is for what is for sale.

        Kept as a section rather than deleted, because D90 leaves the door open
        to a paid game and the day one exists is the day it needs a shelf. With
        none, there is no heading either: an empty "Games" would advertise that
        we sell none.
      */}
      {catalog.games.some((game) => !game.free) ? (
        <>
          <h2 className="section-title">Games</h2>
          <div className="game-grid">
            {catalog.games
              .filter((game) => !game.free)
              .map((game) => (
                <GameCard key={game.id} game={game} showOwned />
              ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

